/**
 * Client-safe shapes for server-built download ZIPs (migration 1320).
 * Server helpers live in `download-zip-jobs.ts`; keep this module free
 * of `node:` and service-role imports so client components can use it.
 */

export type DownloadZipScope = "lidar_raw" | "as_built_package" | "revision";

export type DownloadZipStatus = "pending" | "building" | "ready" | "failed";

/**
 * What a request route answers. `stream` means the selection is small
 * enough (or the server-built path is unavailable) and the caller should
 * keep its in-browser / streamed ZIP.
 */
export type DownloadZipJobView =
  | { mode: "stream" }
  | {
      mode: "job";
      status: DownloadZipStatus;
      fileName: string;
      bytesDone: number;
      bytesTotal: number | null;
      filesDone: number;
      filesTotal: number | null;
      fileSize: number | null;
      /** Waiting for a free build slot rather than building. */
      queued: boolean;
      error: string | null;
      /** Signed R2 URL, only when `status === "ready"`. */
      url: string | null;
    };

/** Raw LiDAR selections at or above this are built on the server. */
export const LIDAR_RAW_SERVER_ZIP_MIN_BYTES = 2 * 1024 ** 3;

/**
 * As-Built package and version ZIPs at or above this are built on the
 * server; smaller ones still fit the 300 s streamed route.
 */
export const FILE_ASSET_SERVER_ZIP_MIN_BYTES = 1024 ** 3;

export function isDownloadZipJobView(value: unknown): value is DownloadZipJobView {
  if (!value || typeof value !== "object") return false;
  const v = value as { mode?: unknown; status?: unknown };
  if (v.mode === "stream") return true;
  return (
    v.mode === "job" &&
    (v.status === "pending" ||
      v.status === "building" ||
      v.status === "ready" ||
      v.status === "failed")
  );
}
